<!--
/**
 * APCWO customisation for OrangeHRM — Event Notifications module.
 *
 * Licensed under the GNU General Public License v2.0 or later, the same terms
 * as OrangeHRM itself. See the LICENSE file at the project root.
 */
-->

<template>
  <oxd-form :loading="loading" @submit-valid="onSubmit">
    <oxd-form-row>
      <oxd-grid :cols="2" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.name"
            label="Name"
            :rules="rules.name"
            required
          />
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.eventType"
            type="select"
            label="Event type"
            :options="eventTypeOptions"
            :rules="rules.eventType"
            :show-empty-selector="false"
            required
            @update:model-value="onEventTypeChange"
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>

    <oxd-form-row>
      <oxd-input-field
        v-model="form.template"
        type="select"
        label="Template"
        :options="templateOptions"
        :rules="rules.template"
        :show-empty-selector="false"
        :disabled="templateOptions.length === 0"
        required
      />
      <oxd-text
        v-if="templateOptions.length === 0"
        tag="p"
        class="apcwo-event-notification-form__hint"
      >
        No templates found for this event type yet — create one under Admin ›
        Configuration › Email Templates first.
      </oxd-text>
    </oxd-form-row>

    <oxd-divider />

    <oxd-text tag="p" class="apcwo-event-notification-form__section">
      Schedule
    </oxd-text>
    <oxd-form-row>
      <oxd-grid :cols="3" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.runTime"
            type="time"
            label="Run time"
            placeholder="hh:mm"
            :rules="rules.runTime"
            required
          />
        </oxd-grid-item>
        <oxd-grid-item>
          <timezone-dropdown v-model="form.timezone" required />
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.active"
            type="select"
            label="Status"
            :options="activeOptions"
            :show-empty-selector="false"
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>

    <oxd-divider />

    <oxd-text tag="p" class="apcwo-event-notification-form__section">
      Send a test
    </oxd-text>
    <oxd-form-row>
      <oxd-grid :cols="2" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="testEmail"
            label="Send test email to"
            :rules="rules.email"
          />
        </oxd-grid-item>
        <oxd-grid-item class="apcwo-event-notification-form__test-action">
          <oxd-button
            type="button"
            display-type="secondary"
            label="Send test email"
            :disabled="sendingTest || !form.template"
            @click="onSendTest"
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>

    <oxd-divider />

    <oxd-form-actions>
      <required-text />
      <oxd-button
        type="button"
        display-type="ghost"
        label="Cancel"
        @click="$emit('cancel')"
      />
      <oxd-button type="submit" display-type="secondary" :label="submitLabel" />
    </oxd-form-actions>
  </oxd-form>
</template>

<script>
import {required, validEmailFormat} from '@ohrm/core/util/validation/rules';
import {APIService} from '@ohrm/core/util/services/api.service';
import TimezoneDropdown from '@/orangehrmAttendancePlugin/components/TimezoneDropdown.vue';

const EVENT_TYPES = [
  {id: 'birthday', label: 'Birthday'},
  {id: 'work_anniversary', label: 'Work Anniversary'},
  {id: 'public_holiday', label: 'Public Holiday'},
];
const ACTIVE_OPTIONS = [
  {id: true, label: 'Active'},
  {id: false, label: 'Inactive'},
];

const asOption = (list, value, fallback) => {
  const found = list.find((o) => o.id === value);
  return found || fallback;
};

export default {
  name: 'EventNotificationForm',
  components: {
    'timezone-dropdown': TimezoneDropdown,
  },
  props: {
    initialValue: {
      type: Object,
      default: () => ({}),
    },
    submitLabel: {
      type: String,
      default: 'Save',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit', 'cancel'],
  setup() {
    const templateHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/apcwo/email-templates',
    );
    const testHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/apcwo/event-notifications/test',
    );
    testHttp.setIgnorePath('/api/v2/apcwo/event-notifications/test');
    return {templateHttp, testHttp};
  },
  data() {
    const v = this.initialValue || {};
    return {
      eventTypeOptions: EVENT_TYPES,
      activeOptions: ACTIVE_OPTIONS,
      templateOptions: [],
      testEmail: '',
      sendingTest: false,
      form: {
        name: v.name || '',
        eventType: asOption(EVENT_TYPES, v.eventType, EVENT_TYPES[0]),
        template:
          v.templateId != null
            ? {id: v.templateId, label: v.templateName}
            : null,
        runTime: v.runTime || '07:00',
        timezone: v.timezone ? {id: v.timezone, label: v.timezone} : null,
        active: asOption(ACTIVE_OPTIONS, v.active, ACTIVE_OPTIONS[0]),
      },
      rules: {
        name: [required],
        eventType: [required],
        template: [required],
        runTime: [required],
        email: [(val) => val === '' || val == null || validEmailFormat(val)],
      },
    };
  },
  computed: {
    eventTypeId() {
      return this.form.eventType ? this.form.eventType.id : null;
    },
  },
  created() {
    this.loadTemplates(this.eventTypeId);
    this.testHttp
      .getAll()
      .then((response) => {
        this.testEmail = response.data.data.defaultTestEmail || '';
      })
      .catch(() => {
        // no default available — leave the field blank
      });
  },
  methods: {
    onEventTypeChange() {
      this.form.template = null;
      this.loadTemplates(this.eventTypeId);
    },
    loadTemplates(eventType) {
      if (!eventType) {
        this.templateOptions = [];
        return;
      }
      this.templateHttp
        .getAll({eventType, limit: 100, sortField: 'et.name', sortOrder: 'ASC'})
        .then((response) => {
          this.templateOptions = (response.data.data || []).map((t) => ({
            id: t.id,
            label: t.name,
          }));
          if (this.form.template) {
            const stillValid = this.templateOptions.find(
              (o) => o.id === this.form.template.id,
            );
            this.form.template = stillValid || this.form.template;
          }
        });
    },
    onSubmit() {
      this.$emit('submit', {
        name: this.form.name,
        eventType: this.form.eventType ? this.form.eventType.id : null,
        templateId: this.form.template ? this.form.template.id : null,
        runTime: this.form.runTime,
        timezone: this.form.timezone ? this.form.timezone.id : null,
        active: this.form.active ? this.form.active.id : true,
      });
    },
    onSendTest() {
      if (!this.form.template) return;
      if (!this.testEmail || validEmailFormat(this.testEmail) !== true) {
        this.$toast.error({
          title: 'Error',
          message: 'Enter a valid email address to send the test to',
        });
        return;
      }
      this.sendingTest = true;
      this.testHttp
        .create({
          testEmail: this.testEmail,
          eventType: this.eventTypeId,
          templateId: this.form.template.id,
        })
        .then((response) => {
          this.$toast.success({
            title: 'Sent',
            message: `Test email sent to ${response.data.data.sentTo}`,
          });
        })
        .catch((e) => {
          const message = e?.response?.data?.error?.message;
          this.$toast.error({
            title: 'Error',
            message: message || 'Test email could not be sent',
          });
        })
        .finally(() => {
          this.sendingTest = false;
        });
    },
  },
};
</script>

<style scoped>
.apcwo-event-notification-form__section {
  font-weight: 700;
  font-size: 13px;
  margin: 8px 0;
}
.apcwo-event-notification-form__test-action {
  display: flex;
  align-items: flex-end;
}
.apcwo-event-notification-form__hint {
  font-size: 12px;
  color: #6b6b6b;
  margin-top: 4px;
}
</style>
